import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter,
  Input, OnInit,
  Output, Renderer2, TemplateRef,
  ViewChild
} from '@angular/core';

@Component({
    templateUrl: './first-time-highlight.component.html',
    styleUrls: ['./first-time-highlight.component.scss'],
    standalone: false
})
export class FirstTimeHighlightComponent implements OnInit, AfterViewInit {

  @ViewChild('highlight') elementRef: ElementRef;

  @Input() host: HTMLElement;
  @Input() content?: string | number;
  @Input() template?: TemplateRef<any>;
  @Input() position: 'top'|'bottom'|'left'|'right';
  @Input() align: 'top'|'bottom'|'left'|'right'|'center';
  @Input() size: 'small'|'medium';
  @Input() highlightedElement?: string; // if we want to highlight child element/elements
  @Input() customStyles?: string = ''; // add some classes that does not get with getStylesWithoutDefaults function4
  @Input() hasTooltip?: boolean; // show blue tooltip with some position near to highlighted element
  @Input() withShadow?: boolean;
  @Input() animation?: boolean;
  @Input() hasCloseBtn?: boolean;
  @Input() showBtn: boolean = true;
  @Input() cloneElement?: boolean = true;
  @Input() buttonText?: string = 'Click Me!';
  @Output() btnEvent = new EventEmitter<any>();

  public elementStyle = {};
  public loading: boolean;

  constructor(
    private renderer2: Renderer2,
    private _element: ElementRef,
  ) {}

  ngOnInit() {
    if (this.cloneElement) {
      const styles = this.getStylesWithoutDefaults(this.host);
      const hostElementCopy = <HTMLElement>(this.host.cloneNode(true));
      hostElementCopy.classList.add('db-first-time-highlight__copy');
      if (!this.highlightedElement && this.customStyles) {
        hostElementCopy.style.cssText = styles + this.customStyles;
      }

      const parentElement = (<HTMLElement>document.getElementById('db-first-time-highlight__copy-parent'));
      parentElement.style.cssText = "top: " + this.host.getBoundingClientRect().top + "px; left: " + (this.host.getBoundingClientRect().right - this.host.offsetWidth ) + "px;";
      parentElement.appendChild(hostElementCopy);
      this.checkIfHasHighlightedElement(hostElementCopy);
    }
  }

  public nextStep(btnClicked: boolean = false) {
    if (btnClicked) {
      this.loading = true;
    }
    this.btnEvent.emit('clicked');
  }

  ngAfterViewInit() {
    this.renderElement();
  }

  private checkIfHasHighlightedElement(hostElementCopy): void {
    /* add highlighted-element class to highlighted element for pulsing */
    /* if there is highlightedElement to get add class to that element for pulsing animation */
    /* else if there is no highlightedElement add class to host element */

    let highlightedElement = hostElementCopy;
    if (this.highlightedElement && hostElementCopy.getElementsByClassName(this.highlightedElement).length) {
      const getHighlightedElements = hostElementCopy.getElementsByClassName(this.highlightedElement);
      for (let i = 0; i < getHighlightedElements.length; i++) {
        highlightedElement = getHighlightedElements[i];
        highlightedElement.classList.add('highlighted-element');
        if (this.customStyles) {
          this._updateElementStyles();
        }
      }
    }
    if (this.animation) {
      document.getElementsByClassName('highlighted-element')[0].classList.add('animated');
    }
  }

  private _updateElementStyles(): void {
    const highlightedElement = document.querySelector(`.${this.highlightedElement}`) as HTMLElement;

    if (highlightedElement) {
      this.renderer2.removeAttribute(highlightedElement, 'style');

      this.renderer2.setAttribute(highlightedElement, 'style', this.customStyles);
    }
  }

  private getStylesWithoutDefaults( element ) {

    // creating an empty object to compare with
    const dummy = document.createElement( 'element-' + ( new Date().getTime() ) );
    document.body.appendChild( dummy );

    // getting computed styles for both elements
    const defaultStyles = getComputedStyle( dummy );
    const elementStyles = getComputedStyle( element );

    // calculating the difference
    const diff = {};
    for ( const key in elementStyles ) {
      if (elementStyles.hasOwnProperty(key)
        && defaultStyles[ key ] !== elementStyles[ key ] ) {
        diff[ key ] = elementStyles[ key ];
      }
    }

    // clear dom
    dummy.remove();
    let cssText = '';
    if (!cssText) {
      cssText = Object.keys(diff).reduce((str, property) => {
        return `${str}${property}:${diff[property]};`;
      }, '');
    }

    return cssText;
  }

  private renderElement(): void {
    this.elementStyle = {};
    const hostBoundingBox: ClientRect = this.host.getBoundingClientRect();
    const elementBoundingBox: ClientRect = this.elementRef.nativeElement.getBoundingClientRect();
    let top: number;
    let left: number;
    let padding: string;

    switch (this.position) {
      case 'top':
        if (hostBoundingBox.top - elementBoundingBox.height + 10 > 0) {
          top = hostBoundingBox.top - elementBoundingBox.height - 10;
          padding = '0 0 10px 0';
        } else {
          top = hostBoundingBox.bottom;
          padding = '10px 0 0 0';
        }
        break;

      case 'bottom':
        if (hostBoundingBox.bottom + elementBoundingBox.height + 10 <= window.innerHeight) {
          top = hostBoundingBox.bottom;
          padding = '10px 0 0 0';
        } else {
          top = hostBoundingBox.top - elementBoundingBox.height - 10;
          padding = '0 0 10px 0';
        }
        break;

      case 'left':
        top = hostBoundingBox.top + hostBoundingBox.height / 2 - elementBoundingBox.height / 2;
        left = hostBoundingBox.left - elementBoundingBox.width - 10;
        padding = '0 10px 0 0';
        break;

      case 'right':
        top = hostBoundingBox.top + hostBoundingBox.height / 2 - elementBoundingBox.height / 2;
        left = hostBoundingBox.right;
        padding = '0 0 0 10px';
        break;
    }

    if (this.position === 'top' || this.position === 'bottom') {
      switch (this.align) {
        case "center":
          left = hostBoundingBox.left + hostBoundingBox.width / 2 - elementBoundingBox.width / 2;
          break;
        case "left":
          left = hostBoundingBox.left;
          break;
        case "right":
          left = hostBoundingBox.right - elementBoundingBox.width;
          break;
      }
    }

    if (this.position === 'left' || this.position === 'right') {
      switch (this.align) {
        case "top":
          top = hostBoundingBox.top + hostBoundingBox.height - elementBoundingBox.height;
          break;
        case "bottom":
          top = hostBoundingBox.top;
          break;
      }
    }

    this.elementStyle = {
      top: top + 'px',
      left: left + 'px',
      padding: padding
    };
  }
}
